import * as moment from 'moment';
import { createReducer, on } from '@ngrx/store';
import * as gptActions from '../actions/gpt.actions';
import * as userActions from '../actions/user.actions';
import { ISubscription, IUser } from '@metutors/core/models';
import { UserRole, generalConstants } from '@metutors/config';

export interface State {
  // Sign up
  ipApi: any;
  affiliate: string;
  returnUrl: string;
  isSignUp: boolean;
  signUpError: string;
  registerStep: number;
  registerEmail: string;
  isVerifyEmail: boolean;
  isSocialSignIn: boolean;
  registerUserType: number;
  registerMobileNumber: string;
  isResendEmailConfirm: boolean;

  // Sign in
  token?: string;
  tutorUID: string;
  isSignIn: boolean;
  tempToken?: string;
  signUpTitle: string;
  signInFailure?: string;
  openSignInModal: boolean;
  signInSubHeading: string;
  openSignUpModal: boolean;
  isSubmitOTPAdmin: boolean;
  isResendOTPAdmin: boolean;
  showEmailVerificationModal: boolean;

  // Complete profile
  isDemo: number;
  timezone?: string;
  user: IUser | null;
  profileStep: number;
  subscription: ISubscription | null;

  // News Letter
  isSignNewsLetter: boolean;
  isVerifyNewsLetter: boolean;
  verifyNewsLetterError: boolean;
}

export const initialState: State = {
  isDemo: 0,
  user: null,
  ipApi: null,
  timezone: '',
  tutorUID: '',
  returnUrl: '',
  affiliate: '',
  profileStep: 1,
  registerStep: 1,
  signUpTitle: '',
  signUpError: '',
  isSignIn: false,
  isSignUp: false,
  registerEmail: '',
  subscription: null,
  signInSubHeading: '',
  isVerifyEmail: false,
  isSocialSignIn: false,
  openSignInModal: false,
  openSignUpModal: false,
  isSubmitOTPAdmin: false,
  isSignNewsLetter: false,
  isResendOTPAdmin: false,
  registerMobileNumber: '',
  isVerifyNewsLetter: false,
  isResendEmailConfirm: false,
  verifyNewsLetterError: false,
  showEmailVerificationModal: false,
  registerUserType: UserRole.student,
};

export const reducer = createReducer(
  initialState,

  on(
    userActions.identifyUserSuccess,
    (state, { profileStep, user, isDemo, timezone }) => ({
      ...state,
      user,
      isDemo,
      timezone,
      profileStep,
    })
  ),

  on(userActions.resetRegisterError, state => ({
    ...state,
    signUpError: '',
  })),

  on(userActions.register, state => ({
    ...state,
    isSignUp: true,
    signUpError: '',
  })),

  on(
    userActions.registerSuccess,
    (state, { email, userType, isModal, mobileNumber }) => ({
      ...state,
      isSignUp: false,
      signUpError: '',
      registerEmail: email,
      registerUserType: userType,
      registerStep: isModal ? 1 : 2,
      registerMobileNumber: mobileNumber!,
      showEmailVerificationModal: isModal ? true : false,
    })
  ),

  on(userActions.registerFailure, (state, { error }) => ({
    ...state,
    isSignUp: false,
    signUpError: error && error?.length ? error[0] : error,
  })),

  on(userActions.registerStep, (state, { step, email, userType }) => ({
    ...state,
    registerStep: step,
    registerEmail: email,
    registerUserType: userType,
  })),

  on(userActions.getIpApiSuccess, (state, { ipApi }) => ({
    ...state,
    ipApi,
  })),

  on(userActions.changeCountry, (state, { country }) => ({
    ...state,
    ipApi: state.ipApi
      ? {
          ...state.ipApi,
          countryCode: country?.iso2!,
          countryName: country?.nameEn!,
        }
      : {
          countryCode: country?.iso2!,
          countryName: country?.nameEn!,
        },
  })),

  on(userActions.signIn, state => ({
    ...state,
    isSignIn: true,
    signInFailure: '',
  })),

  on(userActions.socialSignIn, state => ({
    ...state,
    isSocialSignIn: true,
    signInFailure: '',
  })),

  on(
    userActions.signInSuccess,
    (state, { token, profileStep, user, isDemo, timezone, subscription }) => ({
      ...state,
      user,
      token,
      isDemo,
      timezone,
      profileStep,
      subscription,
      isSignIn: false,
      signInFailure: '',
      tempToken: undefined,
      isSocialSignIn: false,
      isSubmitOTPAdmin: false,
    })
  ),

  on(
    userActions.signInAdminSuccess,
    (state, { tempToken, user, subscription }) => ({
      ...state,
      user,
      tempToken,
      subscription,
      isSignIn: false,
      signInFailure: '',
      isSocialSignIn: false,
    })
  ),

  on(userActions.signInFailure, (state, { error }) => ({
    ...state,
    isSignIn: false,
    isSocialSignIn: false,
    signInFailure: error,
    isSubmitOTPAdmin: false,
  })),

  on(userActions.signInFailure, (state, { errorInfo }) => ({
    ...state,
    showEmailVerificationModal: +errorInfo?.verified! === 0 ? true : false,
  })),

  on(userActions.openAccountEmailVerificationModal, state => ({
    ...state,
    showEmailVerificationModal: true,
  })),

  on(
    userActions.signInSuccess,
    userActions.verifyEmailSuccess,
    userActions.closeAccountEmailVerificationModal,
    state => ({
      ...state,
      showEmailVerificationModal: false,
    })
  ),

  on(
    userActions.openSignInModal,
    (state, { uid, subHeading, returnUrl, affiliate }) => ({
      ...state,
      returnUrl,
      affiliate,
      tutorUID: uid,
      openSignInModal: true,
      signInSubHeading: subHeading,
    })
  ),

  on(userActions.closeSignInModal, state => ({
    ...state,
    openSignInModal: false,
  })),

  on(
    userActions.openSignUpModal,
    (state, { title, returnUrl, affiliate, role }) => ({
      ...state,
      returnUrl,
      affiliate,
      signUpTitle: title,
      openSignUpModal: true,
      registerUserType: role ? role : state.registerUserType,
    })
  ),

  on(userActions.closeSignUpModal, state => ({
    ...state,
    openSignUpModal: false,
  })),

  on(userActions.routeSignInModal, userActions.signInSuccess, state => ({
    ...state,
    openSignInModal: false,
    openSignUpModal: false,
  })),

  on(userActions.submitOTPAdmin, state => ({
    ...state,
    signInFailure: '',
    isSubmitOTPAdmin: true,
  })),

  on(userActions.resendOTPAdmin, state => ({
    ...state,
    signInFailure: '',
    isResendOTPAdmin: true,
  })),

  on(
    userActions.resendOTPAdminSuccess,
    userActions.resendOTPAdminFailure,
    state => ({
      ...state,
      isResendOTPAdmin: false,
      signInFailure: '',
    })
  ),

  on(gptActions.gptDestroyIntroJSSuccess, (state, { user, token }) => ({
    ...state,
    user,
    token,
  })),

  on(
    gptActions.getGPTFreeTrialSuccess,
    gptActions.unsubscribeGPTPackageSuccess,
    gptActions.verifyGPTPackageSubscriptionSuccess,
    (state, { subscription, token }) => ({
      ...state,
      token,
      subscription,
    })
  ),

  on(userActions.resendEmailConfirm, state => ({
    ...state,
    isResendEmailConfirm: true,
  })),

  on(
    userActions.resendEmailConfirmSuccess,
    userActions.resendEmailConfirmFailure,
    state => ({
      ...state,
      isResendEmailConfirm: false,
    })
  ),

  on(gptActions.loadUserGPTSubscriptionSuccess, (state, { subscription }) => ({
    ...state,
    subscription,
  })),

  on(userActions.signupNewsLetter, state => ({
    ...state,
    isSignNewsLetter: true,
  })),

  on(
    userActions.signupNewsLetterSuccess,
    userActions.signupNewsLetterFailure,
    state => ({
      ...state,
      isSignNewsLetter: false,
    })
  )
);

export const selectToken = (state: State): string | undefined => state.token;

export const selectTempToken = (state: State): string | undefined =>
  state.tempToken;

export const selectSignInFailure = (state: State): string | undefined =>
  state.signInFailure;

export const selectIsSignUp = (state: State): boolean => state.isSignUp;

export const selectSignUpError = (state: State): string => state.signUpError;

export const selectIsSignNewsLetter = (state: State): boolean =>
  state.isSignNewsLetter;

export const selectIsVerifyNewsLetter = (state: State): boolean =>
  state.isVerifyNewsLetter;

export const selectVerifyNewsLetterError = (state: State): boolean =>
  state.verifyNewsLetterError;

export const selectRegisterStep = (state: State): number => state.registerStep;

export const selectIpApi = (state: State): string => state.ipApi;

export const selectRegisterEmail = (state: State): string =>
  state.registerEmail;

export const selectRegisterMobileNumber = (state: State): string =>
  state.registerMobileNumber;

export const selectRegisterUserType = (state: State): number =>
  state.registerUserType;

export const selectIsSignIn = (state: State): boolean => state.isSignIn;

export const selectIsSocialSignIn = (state: State): boolean =>
  state.isSocialSignIn;

export const selectIsOpenSignInModal = (state: State): boolean =>
  state.openSignInModal;

export const selectSignInSubHeading = (state: State): string =>
  state.signInSubHeading;

export const selectIsOpenSignUpModal = (state: State): boolean =>
  state.openSignUpModal;

export const selectSignUpTitle = (state: State): string => state.signUpTitle;

export const selectReturnUrl = (state: State): string => state.returnUrl;

export const selectAffiliate = (state: State): string => state.affiliate;

export const selectTutorUID = (state: State): string => state.tutorUID;

export const selectIsSubmitOTPAdmin = (state: State): boolean =>
  state.isSubmitOTPAdmin;

export const selectIsResendOTPAdmin = (state: State): boolean =>
  state.isResendOTPAdmin;

export const selectProfileStep = (state: State): number => state.profileStep;

export const selectStudentIsDemo = (state: State): number => state.isDemo;

export const selectUserTimezone = (state: State): string => state.timezone!;

export const selectUser = (state: State): IUser | null => state.user;

export const selectSubscription = (state: State): ISubscription | null =>
  state.subscription;

export const selectIsVerifyEmail = (state: State): boolean =>
  state.isVerifyEmail;

export const selectIsResendEmailConfirm = (state: State): boolean =>
  state.isResendEmailConfirm;

export const selectShowEmailVerificationModal = (state: State): boolean =>
  state.showEmailVerificationModal;

export const selectUserGPTRemaingDays = (state: State): number => {
  if (state.subscription?.updatedAt) {
    const deadline = new Date(state.subscription?.updatedAt);
    deadline.setDate(
      deadline.getDate() + generalConstants.trialSubscriptionDays
    );

    const endDate = moment(deadline, 'YYYY-MM-DD');
    const currentDate = moment().startOf('day');

    return Math.floor(moment.duration(endDate.diff(currentDate)).asDays());
  }

  return 0;
};
